// src/pages/employer/EmployerWorkplaces.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { orgAPI } from '../../services/api';
import { useConfirm, EditBtn, AddBtn, DelBtn, usePersistedState } from '../../components/shared/UI';
import {
  SectionModal, AddressModal, LanguagesModal, LanguagePicker,
  formatAddress, EMPTY_ADDRESS, fieldModalInputStyle, fieldModalLabelStyle,
} from '../../components/shared/FieldModals';
import {
  MapPin, X, Building2, ChevronUp, ChevronDown,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────

const ENVIRONMENT_OPTIONS = [
  { value: 'OFFICE',         label: 'Office' },
  { value: 'TRANSPORTATION', label: 'Transportation (car / truck / train / bus / boat etc.)' },
  { value: 'EDUCATIONAL',    label: 'Educational (schools / universities etc.)' },
  { value: 'RETAIL',         label: 'Retail (shopping centre, stores etc.)' },
  { value: 'HOSPITALITY',    label: 'Hospitality / Hotel' },
  { value: 'MANUFACTURING',  label: 'Manufacturing (factory)' },
  { value: 'UNIVERSITY',     label: 'University / school' },
  { value: 'GOVERNMENT',     label: 'Government' },
  { value: 'CONSTRUCTION',   label: 'Construction' },
  { value: 'OUTDOORS',       label: 'Outdoors' },
  { value: 'HEALTHCARE',     label: 'Healthcare (hospitals, medical centers etc.)' },
  { value: 'FARMING',        label: 'Farming / Agriculture' },
  { value: 'OTHER',          label: 'Other' },
];
const ENVIRONMENT_LABEL = Object.fromEntries(ENVIRONMENT_OPTIONS.map(({ value, label }) => [value, label]));

// ─── Local style helpers ───────────────────────────────────

const inputStyle      = fieldModalInputStyle;
const labelStyle      = fieldModalLabelStyle;
const fieldWrap       = { marginBottom: 14 };

// ─── WorkplaceCard ────────────────────────────────────────

const WorkplaceCard = ({ workplace, isAdmin, onSaved, onDeleted }) => {
  const { confirm, dialog } = useConfirm();
  const { activeOrg, activeProfile } = useAuth();
  const [wp, setWp]         = useState(workplace);
  // modal: null | 'name' | 'environment' | 'address' | 'languages' | 'headcount'
  const [modal, setModal]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen]     = usePersistedState(`workplaces.card.${workplace.id}.open`, true);

  const [nameForm, setNameForm] = useState(null);
  const [envForm,  setEnvForm]  = useState(null);
  const [hcForm,   setHcForm]   = useState(null);

  const save = async (patch) => {
    const { data } = await orgAPI.updateWorkplace(activeOrg.id, activeProfile.id, wp.id, patch);
    const updated = data.data.workplace;
    setWp(updated);
    onSaved(updated);
  };

  // ── Name & Default ──
  const openName = () => {
    setNameForm({ name: wp.name || '', isDefault: wp.isDefault || false });
    setModal('name');
  };
  const saveName = async () => {
    if (!nameForm.name.trim()) return;
    setSaving(true);
    try { await save({ name: nameForm.name.trim(), isDefault: nameForm.isDefault }); setModal(null); }
    finally { setSaving(false); }
  };

  // ── Environment ──
  const openEnvironment = () => { setEnvForm(wp.environment || ''); setModal('environment'); };
  const saveEnvironment = async () => {
    setSaving(true);
    try { await save({ environment: envForm || null }); setModal(null); }
    finally { setSaving(false); }
  };

  // ── Headcount ──
  const openHeadcount = () => { setHcForm(wp.headcount != null ? String(wp.headcount) : ''); setModal('headcount'); };
  const saveHeadcount = async () => {
    setSaving(true);
    try { await save({ headcount: hcForm !== '' ? parseInt(hcForm, 10) : null }); setModal(null); }
    finally { setSaving(false); }
  };

  const closeModal = () => setModal(null);

  const handleDelete = async () => {
    const ok = await confirm('Delete Workplace', `Delete "${wp.name}"? This cannot be undone.`);
    if (!ok) return;
    await orgAPI.deleteWorkplace(activeOrg.id, activeProfile.id, wp.id);
    onDeleted(wp.id);
  };

  const addrStr   = formatAddress(wp.address);
  const languages = Array.isArray(wp.workingLanguages) ? wp.workingLanguages : [];

  const emptyText = { fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' };

  const fieldRow = {
    display: 'flex', alignItems: 'flex-start',
    padding: '11px 16px',
  };
  const fieldLabel = {
    fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    minWidth: 110, flexShrink: 0, paddingTop: 2,
    textAlign: 'right',
  };
  const fieldValue = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 14px' };

  const FieldEditBtn = ({ onClick }) => isAdmin ? (
    <EditBtn onClick={onClick} />
  ) : null;

  return (
    <>
      {dialog}

      {/* ── Name modal ── */}
      {modal === 'name' && nameForm && (
        <SectionModal title="Edit Workplace Name" onSave={saveName} onCancel={closeModal} saving={saving}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Workplace Name</label>
            <input type="text" value={nameForm.name}
              onChange={(e) => setNameForm((f) => ({ ...f, name: e.target.value }))}
              style={inputStyle} placeholder="e.g. Main Office" autoFocus />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id={`wpDefault-${wp.id}`} checked={nameForm.isDefault}
              onChange={(e) => setNameForm((f) => ({ ...f, isDefault: e.target.checked }))}
              style={{ width: 15, height: 15 }} />
            <label htmlFor={`wpDefault-${wp.id}`} style={{ fontSize: 13, cursor: 'pointer' }}>
              Set as Default workplace
            </label>
          </div>
        </SectionModal>
      )}

      {/* ── Environment modal ── */}
      {modal === 'environment' && envForm !== null && (
        <SectionModal title="Edit Environment" onSave={saveEnvironment} onCancel={closeModal} saving={saving}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Environment</label>
            <select value={envForm} onChange={(e) => setEnvForm(e.target.value)} style={inputStyle} autoFocus>
              <option value="">— Select —</option>
              {ENVIRONMENT_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </SectionModal>
      )}

      {/* ── Address modal — uses shared AddressModal ── */}
      {modal === 'address' && (
        <AddressModal
          initialValue={wp.address || EMPTY_ADDRESS}
          onSave={async (addr) => { await save({ address: addr }); setModal(null); }}
          onCancel={closeModal}
        />
      )}

      {/* ── Languages modal — uses shared LanguagesModal ── */}
      {modal === 'languages' && (
        <LanguagesModal
          initialValue={languages}
          onSave={async (langs) => { await save({ workingLanguages: langs }); setModal(null); }}
          onCancel={closeModal}
        />
      )}

      {/* ── Headcount modal ── */}
      {modal === 'headcount' && hcForm !== null && (
        <SectionModal title="Edit Headcount" onSave={saveHeadcount} onCancel={closeModal} saving={saving}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Headcount</label>
            <input type="number" min="0" value={hcForm}
              onChange={(e) => setHcForm(e.target.value)}
              style={inputStyle} placeholder="e.g. 50" autoFocus />
          </div>
        </SectionModal>
      )}

      {/* ── Card ── */}
      <div className="section-box fade-in" style={{ marginBottom: 20 }}>

        {/* Header */}
        <div
          className="section-box-header"
          onClick={() => setOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Building2 size={15} />
            <span>{wp.name}</span>
            {wp.isDefault && (
              <span style={{
                fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)', color: 'white',
                borderRadius: 10, padding: '1px 7px', letterSpacing: '0.03em',
              }}>DEFAULT</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
            {isAdmin && (
              <>
                <EditBtn onClick={openName} title="Edit workplace name" />
                <DelBtn onClick={handleDelete} title="Delete workplace" />
              </>
            )}
            <span onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', marginLeft: 4, cursor: 'pointer' }}>
              {open
                ? <ChevronUp size={14} style={{ color: 'rgba(255,255,255,0.85)' }} />
                : <ChevronDown size={14} style={{ color: 'rgba(255,255,255,0.85)' }} />}
            </span>
          </div>
        </div>

        {/* ── Field list ── */}
        {open && <>

        {/* Environment */}
        <div style={fieldRow}>
          <div style={fieldLabel}>Environment:</div>
          <div style={fieldValue}>
            {wp.environment
              ? <span className="tag">{ENVIRONMENT_LABEL[wp.environment] || wp.environment}</span>
              : <span style={emptyText}>Not set</span>
            }
            <FieldEditBtn onClick={openEnvironment} />
          </div>
        </div>

        {/* Address */}
        <div style={fieldRow}>
          <div style={fieldLabel}>Address:</div>
          <div style={fieldValue}>
            {addrStr
              ? <span style={{ fontSize: 13, color: 'var(--text)' }}>{addrStr}</span>
              : <span style={emptyText}>Not set</span>
            }
            <FieldEditBtn onClick={() => setModal('address')} />
          </div>
        </div>

        {/* Languages */}
        <div style={fieldRow}>
          <div style={fieldLabel}>Languages:</div>
          <div style={fieldValue}>
            {languages.length > 0
              ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {languages.map((l) => (
                    <span key={l} style={{
                      background: '#EBF3FC', border: '1px solid #3D80CC', color: '#3D80CC',
                      borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 500,
                    }}>{l}</span>
                  ))}
                </div>
              : <span style={emptyText}>Not set</span>
            }
            <FieldEditBtn onClick={() => setModal('languages')} />
          </div>
        </div>

        {/* Headcount */}
        <div style={fieldRow}>
          <div style={fieldLabel}>Headcount:</div>
          <div style={fieldValue}>
            {wp.headcount != null
              ? <span style={{ fontSize: 13, color: 'var(--text)' }}>{wp.headcount} staff</span>
              : <span style={emptyText}>Not set</span>
            }
            <FieldEditBtn onClick={openHeadcount} />
          </div>
        </div>

        </>}
      </div>
    </>
  );
};

// ─── Add Workplace Modal ───────────────────────────────────

const AddWorkplaceModal = ({ onSaved, onCancel, orgId, profileId, isFirst }) => {
  const [name, setName]         = useState(isFirst ? 'Main Office' : '');
  const [isDefault, setDefault] = useState(isFirst);
  const [saving, setSaving]     = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await orgAPI.createWorkplace(orgId, profileId, {
        name: name.trim(), isDefault, workingLanguages: [],
      });
      onSaved(data.data.workplace);
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-box" style={{ maxWidth: 440, width: '90vw' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Add Workplace</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Workplace Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              style={inputStyle} placeholder="e.g. Main Office" autoFocus />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="newWpDefault" checked={isDefault}
              onChange={(e) => setDefault(e.target.checked)}
              style={{ width: 15, height: 15 }} />
            <label htmlFor="newWpDefault" style={{ fontSize: 13, cursor: 'pointer' }}>
              Set as Default workplace
            </label>
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}
            disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create Workplace'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────

export const EmployerWorkplaces = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const [workplaces, setWorkplaces]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [addingNew, setAddingNew]             = useState(false);

  useEffect(() => {
    if (!activeOrg || !activeProfile) return;
    const load = async () => {
      setLoading(true);
      try {
        const wpRes = await orgAPI.getWorkplaces(activeOrg.id, activeProfile.id);
        const fetched = wpRes.data.data.workplaces || [];
        if (fetched.length === 0) {
          try {
            const { data } = await orgAPI.createWorkplace(activeOrg.id, activeProfile.id, {
              name: 'Main Office', isDefault: true, workingLanguages: [],
            });
            setWorkplaces([data.data.workplace]);
          } catch { setWorkplaces([]); }
        } else {
          setWorkplaces(fetched);
        }
      } finally { setLoading(false); }
    };
    load();
  }, [activeOrg, activeProfile]);

  const handleSaved   = (updated) => setWorkplaces((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
  const handleDeleted = (id)      => setWorkplaces((prev) => prev.filter((w) => w.id !== id));
  const handleCreated = (created) => { setWorkplaces((prev) => [...prev, created]); setAddingNew(false); };

  if (!activeOrg || !activeProfile) return (
    <div className="fade-in">
      <div className="page-header"><h1>Workplaces</h1></div>
      <div style={{ padding: 40, color: 'var(--text-muted)', textAlign: 'center' }}>No active profile selected.</div>
    </div>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Workplaces</h1>
        {isAdmin && (
          <AddBtn onClick={() => setAddingNew(true)} size={14} title="Add Workplace" />
        )}
      </div>

      {addingNew && (
        <AddWorkplaceModal
          orgId={activeOrg.id}
          profileId={activeProfile.id}
          isFirst={workplaces.length === 0}
          onSaved={handleCreated}
          onCancel={() => setAddingNew(false)}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 60 }}>Loading…</div>
      ) : (
        <>
          {workplaces.map((wp) => (
            <WorkplaceCard
              key={wp.id}
              workplace={wp}
              isAdmin={isAdmin}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
            />
          ))}
          {workplaces.length === 0 && (
            <div className="section-box">
              <div className="section-box-body" style={{ textAlign: 'center', padding: '48px 20px' }}>
                <MapPin size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 16px' }}>
                  No workplaces added yet.
                </p>
                {isAdmin && (
                  <AddBtn onClick={() => setAddingNew(true)} size={14} title="Add Workplace" />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployerWorkplaces;
